/**
 * 匿名用户身份管理。
 * 未登录用户通过这里获得稳定匿名 ID，后续注册登录时可迁移原有会话数据。
 */
import { ref, onMounted } from "vue";
import { StorageKey } from "~/enums";
import { dateId } from "~/utils";
import { useAuthUser } from "./useAuthUser";

/**
 * 匿名用户 ID 管理
 *
 * 未登录：生成一个固定的匿名 ID（存储在 cookie），整个浏览器会话期间保持不变
 * 已登录：使用用户名或用户 ID
 *
 * 用途：在未登录状态下，为当前浏览器会话生成一个固定的匿名 ID
 * 特点：
 * - 存储在 cookie 中，支持 SSR
 * - 同一用户的所有会话（sessionId）都使用同一个 anonymousId
 * - 便于关联同一用户的不同对话会话
 */
export function useAnonymousUser() {
  const { user } = useAuthUser();
  // 初始为空字符串，确保 SSR 和 CSR 初始渲染一致
  const anonymousId = ref<string>("");

  /**
   * 初始化匿名 ID
   * 仅在客户端挂载后执行，避免 hydration mismatch
   */
  const initAnonymousId = () => {
    if (!import.meta.client) return;

    // 优先从 cookie 读取
    const cookies = document.cookie.split("; ");
    const targetCookie = cookies.find((c) =>
      c.startsWith(`${StorageKey.ANONYMOUS_USER_ID}=`),
    );

    if (targetCookie) {
      anonymousId.value = targetCookie.split("=")[1];
    } else {
      // 如果 cookie 里没有，就生成一个新 ID 并设置到 cookie
      const newId = `anonymous_${dateId()}`;
      anonymousId.value = newId;
      document.cookie = `${StorageKey.ANONYMOUS_USER_ID}=${newId}; max-age=${60 * 60 * 24 * 365}; path=/`;
    }
  };

  onMounted(() => {
    initAnonymousId();
  });

  /**
   * 获取匿名 ID（同步方法，优先从 cookie 读取，避免异步等待 onMounted）
   * 用于在组件挂载前需要获取 userId 的场景（如发送消息时）
   */
  const getAnonymousId = (): string => {
    if (user.value?.id) {
      return user.value.id;
    }

    // 如果已经有值，直接返回
    if (anonymousId.value) {
      return anonymousId.value;
    }

    // 如果在客户端，尝试从 cookie 读取
    if (import.meta.client) {
      const cookies = document.cookie.split("; ");
      const targetCookie = cookies.find((c) =>
        c.startsWith(`${StorageKey.ANONYMOUS_USER_ID}=`),
      );

      if (targetCookie) {
        const id = targetCookie.split("=")[1];
        anonymousId.value = id;
        return id;
      }

      // 没有 cookie 且没有值，生成新 ID 并设置到 cookie
      const newId = `anonymous_${dateId()}`;
      anonymousId.value = newId;
      document.cookie = `${StorageKey.ANONYMOUS_USER_ID}=${newId}; max-age=${60 * 60 * 24 * 365}; path=/`;
      return newId;
    }

    // SSR 环境下返回空字符串
    return "";
  };

  /**
   * 清除匿名 ID（用于测试或切换用户）
   */
  const clearAnonymousId = () => {
    if (!import.meta.client) return;
    anonymousId.value = "";
    document.cookie = `${StorageKey.ANONYMOUS_USER_ID}=; max-age=0; path=/`;
  };

  return {
    anonymousId,
    getAnonymousId,
    clearAnonymousId,
  };
}
